// `status-indication/` ported: the component plus its constant table.
//
// Two screens render it (status, verify) and a third path reaches it through the email
// sign-in variant, which is why it is shared rather than inlined again.
//
// ⚠️ Upstream binds `message` and `info` with `ng-bind-html`, and one entry
// (VerificationCompletedWithEmailSignin) really is markup — a Font Awesome icon inline in
// the message. Rendering that as HTML from a table WE own is safe; rendering it from
// anything the server sends would not be, so the table stays a constant and the component
// takes an entry, never a string.
import { createElement, type ReactElement } from 'react'
import { asset } from '../assetBase.ts'

export const StatusIndicationType = {
  Connected: 'connected',
  Disconnected: 'disconnected',
  VerificationPending: 'verification-pending',
  VerificationCompleted: 'verification-completed',
  VerificationCompletedWithEmailSignin: 'verification-completed-with-email-signin',
} as const
export type StatusIndicationType = (typeof StatusIndicationType)[keyof typeof StatusIndicationType]

export interface StatusIndication {
  name: StatusIndicationType
  icon: string | null
  /** Plain text, except where `messageHtml` says otherwise. */
  message: string
  messageHtml?: string
  info?: string
}

/** Verbatim from `auth-status-indications.constant.ng1.ts`, including the commented-out icon. */
export const StatusIndications: Record<StatusIndicationType, StatusIndication> = {
  [StatusIndicationType.Connected]: {
    name: StatusIndicationType.Connected,
    // Upstream keeps `thumbs-up-solid.svg` commented out above this one; the PNG is live.
    icon: '/static/assets/img/extension/green-check.png',
    message: 'Connected!',
  },
  [StatusIndicationType.Disconnected]: {
    name: StatusIndicationType.Disconnected,
    icon: '/static/assets/img/extension/thumbs-down-solid.svg',
    message: 'Something went wrong',
  },
  [StatusIndicationType.VerificationPending]: {
    name: StatusIndicationType.VerificationPending,
    icon: '/static/assets/img/extension/envelope-solid.svg',
    message: 'Verify your account',
  },
  [StatusIndicationType.VerificationCompleted]: {
    name: StatusIndicationType.VerificationCompleted,
    icon: '/static/assets/img/extension/user-check-solid.svg',
    message: 'Verified!',
    info: 'Email verification completed, you can close this tab now.',
  },
  [StatusIndicationType.VerificationCompletedWithEmailSignin]: {
    name: StatusIndicationType.VerificationCompletedWithEmailSignin,
    icon: null,
    message: 'Email verification completed.',
    messageHtml: '<i class="fad fa-check-circle email-verification-complete"></i> Email verification completed.',
  },
}

export interface StatusIndicationProps {
  status: StatusIndication
  loading?: boolean
}

/**
 * ⚠️ `<status-indication>` is an ELEMENT, and the page stylesheets scope to it.
 * `_status.page.scss` reaches for `status-indication { ... }` — spacing, and the icon
 * filters — while `_status-indication.scss` styles the `.status-indication` class inside it.
 * Emitting only the div matches half the rules and leaves the status screen visibly wrong.
 * Same lesson as `PageHost`: in this codebase the DOM the stylesheet needs is not all in the
 * template.
 */
export function StatusIndicationView({ status, loading = false }: StatusIndicationProps): ReactElement {
  return createElement(
    'status-indication',
    null,
    <div className="status-indication">
      {loading ? (
        <i className="far fa-spinner fa-spin woo-loader" />
      ) : (
        <div className={`status-indication-inner ${status.name}`}>
          {status.icon !== null && <img className="icon" src={asset(status.icon)} alt="" />}
          {status.messageHtml === undefined ? (
            <div className="message">{status.message}</div>
          ) : (
            <div className="message" dangerouslySetInnerHTML={{ __html: status.messageHtml }} />
          )}
          {status.info !== undefined && <div className="info">{status.info}</div>}
        </div>
      )}
    </div>,
  )
}

/** `Object.assign({}, indications[type], { info })` upstream — the status screen's one variant. */
export function statusWithInfo(type: StatusIndicationType, info: string): StatusIndication {
  return { ...StatusIndications[type], info }
}
